import { SourceEngine } from "./SourceEngine.ts";
import { validateProfile } from "./ProfileValidator.ts";
import { resolveUrl } from "./UrlResolver.ts";
import { interpolate } from "./RequestBuilder.ts";
import { discoverSearchFilters } from './SearchFilters.ts';
import { getSourcePolicy, isDynamicSource } from "../network/SourceRegistry.ts";
import type { SourceProfile, RecordValue, OperationDefinition } from "./types.ts";
import type { YomiriHttpClient } from "../network/types.ts";
import type {
  YomiriExtension,
  YomiriManga,
  YomiriChapter,
  YomiriPage,
} from "../types/extension.ts";

export class DeclarativeSource implements YomiriExtension {
  readonly version = "2.0.0";
  get lang() {
    return this.profile.ui?.languages?.[0] ?? "es";
  }
  readonly requiresExtension = false;
  get supportsDownloads() { return this.profile.ui?.chapterDelivery !== 'external-file'; }
  get supportsNewlyAdded() { return !!this.profile.latest && this.profile.ui?.latestOrder !== 'updated'; }
  get supportsLatestUpdates() { return !!this.profile.latest && this.profile.ui?.latestOrder === 'updated'; }
  protected readonly profile: SourceProfile;
  protected readonly engine: SourceEngine;
  private readonly catalog: SourceEngine;
  private readonly latest?: SourceEngine;
  private nextUrls = new Map<string, string>();
  private filterOptions?: ReturnType<typeof discoverSearchFilters>;
  getSearchFilters() {
    return this.filterOptions ??= discoverSearchFilters(this.id, this.profile.baseUrl, this.profile.template ?? '', this.http)
      .catch(error => { this.filterOptions = undefined; throw error; });
  }
  constructor(
    profile: unknown,
    private readonly http: Pick<YomiriHttpClient, "text"> &
      Partial<Pick<YomiriHttpClient, "prepareImage">>,
  ) {
    this.profile = validateProfile(profile);
    this.engine = new SourceEngine(this.profile, http);
    this.catalog = new SourceEngine(
      {
        ...this.profile,
        operations: {
          ...this.profile.operations,
          search: this.profile.catalog ?? this.profile.operations.search,
        },
      },
      http,
    );
    if (this.profile.latest)
      this.latest = new SourceEngine(
        {
          ...this.profile,
          operations: { ...this.profile.operations, search: this.profile.latest },
        },
        http,
      );
  }
  get id() {
    return this.profile.id;
  }
  get name() {
    return this.profile.name;
  }
  get iconUrl() {
    return this.profile.ui?.iconUrl;
  }
  get website() {
    return this.profile.baseUrl;
  }
  get supportsLatest() {
    return !!this.latest;
  }
  get requiredDomains() {
    return this.profile.allowedHosts.map((h) => `https://${h}/*`);
  }
  clearSessionCache() {
    this.filterOptions = undefined;
    this.nextUrls.clear();
  }
  protected resourceUrl(id: string, kind: "manga" | "chapter") {
    if (/^https?:\/\//i.test(id)) return resolveUrl(id, this.profile.baseUrl, this.profile);
    const prefix = kind === "manga" ? this.profile.ui?.mangaPath : this.profile.ui?.chapterPath;
    const safe = id
      .replace(/^\/+|\/+$/g, "")
      .split("/")
      .map((part) => encodeURIComponent(decodeURIComponent(part)))
      .join("/");
    return resolveUrl(
      `${prefix ?? "/"}${safe}${this.profile.ui?.trailingSlash ? "/" : ""}`,
      this.profile.baseUrl,
      this.profile,
    );
  }
  protected resourceId(url: string, kind: "manga" | "chapter") {
    if (this.profile.ui?.idMode === "url") return url;
    const prefix =
      (kind === "manga" ? this.profile.ui?.mangaPath : this.profile.ui?.chapterPath) ?? "/";
    const path = new URL(url).pathname;
    if (!path.startsWith(prefix)) throw new Error("Ruta de recurso inválida.");
    return path.slice(prefix.length).replace(/\/$/, "");
  }
  protected manga(row: RecordValue): YomiriManga {
    const url = resolveUrl(
      typeof row.slug === "string"
        ? `${this.profile.ui?.mangaPath ?? "/"}${encodeURIComponent(row.slug)}`
        : String(row.url),
      this.profile.baseUrl,
      this.profile,
    );
    const coverId = typeof row.slug === 'string' ? row.slug : decodeURIComponent(new URL(url).pathname.split('/').filter(Boolean).pop() ?? '');
    const coverUrl =
      typeof row.coverUrl === "string"
        ? row.coverUrl
        : coverId && this.profile.ui?.coverPath
          ? resolveUrl(
              interpolate(
                this.profile.ui.coverPath,
                { manga: { id: coverId }, baseUrl: this.profile.baseUrl },
                "url",
              ),
              this.profile.baseUrl,
              this.profile,
            )
          : undefined;
    const statusText = String(row.status ?? "").toLowerCase();
    const status = /completed|completado|finalizado/.test(statusText)
      ? "COMPLETED"
      : /ongoing|publicaci|publicándose|en curso/.test(statusText)
        ? "ONGOING"
        : "UNKNOWN";
    return {
      id: this.resourceId(url, "manga"),
      sourceId: this.id,
      title: String(row.title),
      url,
      coverUrl,
      status,
      genres: Array.isArray(row.genres)
        ? row.genres.filter((v): v is string => typeof v === "string")
        : [],
    };
  }
  private paginated(
    engine: SourceEngine,
    definition: OperationDefinition,
    key: string,
    page: number,
  ) {
    const next = this.nextUrls.get(`${key}:${page}`);
    const target = next ? new URL(next) : undefined;
    if (target)
      for (const [name, value] of Object.entries(this.profile.ui?.paginationParams ?? {}))
        target.searchParams.set(name, value);
    return next
      ? new SourceEngine(
          {
            ...this.profile,
            operations: {
              ...this.profile.operations,
              search: {
                ...definition,
                steps: [
                  { ...definition.steps[0], url: target!.href },
                  ...definition.steps.slice(1),
                ],
              },
            },
          },
          this.http,
        )
      : engine;
  }
  async search(query: string, page = 1, filters: Record<string, unknown> = {}) {
    const selected = Object.entries(filters).filter(([, value]) => typeof value === 'string' && value);
    if (selected.length) {
      const definitions = await this.getSearchFilters();
      for (const [key, value] of selected)
        if (!definitions.some(def => def.key === key && def.options.some(option => option.value === value)))
          throw new Error('Filtro no admitido por esta fuente.');
      const key = JSON.stringify(['filtered', query, selected]);
      const next = this.nextUrls.get(`${key}:${page}`);
      const url = new URL(next ?? interpolate(this.profile.operations.search.steps[0].url,
        { baseUrl: this.profile.baseUrl, query, page }, 'url'), this.profile.baseUrl);
      for (const [name, value] of selected) url.searchParams.set(name, String(value));
      const engine = new SourceEngine({ ...this.profile, operations: { ...this.profile.operations,
        search: { ...this.profile.operations.search, steps: [{ ...this.profile.operations.search.steps[0], url: url.href }, ...this.profile.operations.search.steps.slice(1)] } } }, this.http);
      const result = await engine.search({ query, page });
      return this.list(result.items, result.nextUrl, key, page, result.finalUrl);
    }
    const result = await this.paginated(
      this.engine,
      this.profile.operations.search,
      `search:${query}`,
      page,
    ).search({ query, page });
    return this.list(result.items, result.nextUrl, `search:${query}`, page, result.finalUrl);
  }
  private async list(
    items: RecordValue[],
    nextUrl: string | undefined,
    key: string,
    page: number,
    finalUrl: string,
  ) {
    const mangas = [
      ...new Map(
        items.map((row) => {
          const manga = this.manga(row);
          return [manga.id, manga] as const;
        }),
      ).values(),
    ];
    await this.prepareAssets(mangas.flatMap((manga) => (manga.coverUrl ? [manga.coverUrl] : [])));
    const more = nextUrl ? nextUrl !== finalUrl && mangas.length > 0 :
      !!this.profile.ui?.pageSize && mangas.length === this.profile.ui.pageSize;
    if (more) {
      if (nextUrl) this.nextUrls.set(`${key}:${page + 1}`, nextUrl);
      if (this.nextUrls.size > 256) this.nextUrls.delete(this.nextUrls.keys().next().value!);
    } else this.nextUrls.delete(`${key}:${page + 1}`);
    return { mangas, hasNextPage: more };
  }
  async getPopular(page = 1) {
    const result = await this.paginated(
      this.catalog,
      this.profile.catalog ?? this.profile.operations.search,
      "popular",
      page,
    ).search({ query: "", page });
    return this.list(result.items, result.nextUrl, "popular", page, result.finalUrl);
  }
  async getLatest(page = 1) {
    if (!this.latest) return this.getPopular(page);
    const result = await this.paginated(this.latest, this.profile.latest!, "latest", page).search({
      query: "",
      page,
    });
    return this.list(result.items, result.nextUrl, "latest", page, result.finalUrl);
  }
  async getManga(id: string): Promise<YomiriManga> {
    const url = this.resourceUrl(id, "manga");
    const result = await this.engine.detail({ manga: { id, url } });
    const row = result.items[0];
    if (!row) throw new Error("Ficha vacía.");
    if (typeof row.coverUrl === "string") await this.prepareAssets([row.coverUrl]);
    return {
      ...this.manga({ ...row, url }),
      ...(this.profile.ui?.idMode === "path" && !/^https?:/.test(id) ? { id } : {}),
      description: typeof row.description === "string" ? row.description : undefined,
      author: typeof row.author === "string" ? row.author : undefined,
      artist: typeof row.artist === "string" ? row.artist : undefined,
    };
  }
  async getChapters(id: string): Promise<YomiriChapter[]> {
    const url = this.resourceUrl(id, "manga");
    const result = await this.engine.chapters({ manga: { id, url } });
    return [
      ...new Map(
        result.items.map((row) => {
          const chapterUrl = resolveUrl(String(row.url), url, this.profile);
          const name = String(row.name);
          const chapter: YomiriChapter = {
            id: this.resourceId(chapterUrl, "chapter"),
            sourceId: this.id,
            mangaId: id,
            name,
            chapterNumber: Number(name.match(/\d+(?:[.,]\d+)?/)?.[0].replace(",", ".") ?? 0),
            url: chapterUrl,
            ...(this.profile.ui?.chapterDelivery === 'external-file' ? { externalFile: true } : {}),
            language: this.lang === "all" ? undefined : this.lang,
            uploadDate: typeof row.uploadDate === "string" ? row.uploadDate : undefined,
          };
          return [chapter.id, chapter] as const;
        }),
      ).values(),
    ];
  }
  async getPages(id: string): Promise<YomiriPage[]> {
    if (this.profile.ui?.chapterDelivery === 'external-file')
      throw new Error('Esta fuente entrega archivos CBR/CBZ/PDF. Abrí la descarga original desde la lista de tomos.');
    const url = this.resourceUrl(id, "chapter");
    const result = await this.engine.pages({
      chapter: { id, url },
      manga: { id: "", url: this.profile.baseUrl },
    });
    const pages = [
      ...new Set(
        result.items.map((row) =>
          resolveUrl(
            String(row.url),
            result.finalUrl,
            isDynamicSource(this.id)
              ? { allowedHosts: [...getSourcePolicy(this.id).allowedHosts] }
              : this.profile,
          ),
        ),
      ),
    ].map((imageUrl, index) => ({
      index,
      imageUrl,
      sourceId: this.id,
      headers: { Referer: `${this.profile.baseUrl}/` },
    }));
    await this.prepareAssets(pages.map((page) => page.imageUrl));
    return pages;
  }
  private async prepareAssets(urls: string[]): Promise<void> {
    if (!isDynamicSource(this.id) || !this.http.prepareImage) return;
    const byHost = new Map(urls.map((url) => [new URL(url).hostname, url]));
    await Promise.all(
      [...byHost.values()].map(async (url) => {
        const prepared = await this.http.prepareImage!({
          sourceId: this.id,
          url,
          referer: `${this.profile.baseUrl}/`,
        });
        prepared.release();
      }),
    );
  }
  parseUrl(raw: string) {
    try {
      const url = resolveUrl(raw, this.profile.baseUrl, this.profile);
      const path = new URL(url).pathname;
      const chapter = this.profile.ui?.chapterPath;
      const manga = this.profile.ui?.mangaPath;
      if (!manga || (!path.startsWith(manga) && !(chapter && path.startsWith(chapter))))
        return null;
      const isChapter =
        chapter !== manga
          ? !path.startsWith(manga) && path !== "/" && !!chapter && path.startsWith(chapter)
          : path.slice(manga.length).split("/").filter(Boolean).length > 1;
      return {
        type: isChapter ? ("chapter" as const) : ("manga" as const),
        mangaId: isChapter
          ? this.resourceId(url, "chapter").split("/").slice(0, -1).join("/")
          : this.resourceId(url, "manga"),
        ...(isChapter ? { chapterId: this.resourceId(url, "chapter") } : {}),
      };
    } catch {
      return null;
    }
  }
  async checkHealth() {
    try {
      const page = await this.getPopular();
      return {
        ok: page.mangas.length > 0,
        message: page.mangas.length ? "Conectada" : "Sin resultados",
      };
    } catch (error) {
      return { ok: false, message: error instanceof Error ? error.message : "No se pudo conectar" };
    }
  }
}
